<template>
	<div class="modal-overlay">
		<div class="modal-content">
			<div class="modal-header">
				<h3>Annual Review Override</h3>
				<span class="close" @click="handleClose" :class="{ disabled: isSubmitting }">&times;</span>
			</div>

			<div class="modal-body">
				<p>Before performing an override, please confirm the override reasons.</p>
				<form class="form-container">
					<div class="form-input">
						<label for="overrideKnockouts" class="checkbox-label">
							<input
								type="checkbox"
								id="overrideKnockouts"
								name="overrideKnockouts"
								v-model="overrideKnockouts"
								:disabled="isKnockoutCheckboxDisabled"
								:required="requiresKnockoutsOverride" />
							<span class="custom-checkbox"></span>
							Override annual review knockouts
						</label>
					</div>
					<!-- knockouts -->
					<div class="list-columns-container">
						<div class="list-column">
							Borrower
							<ul>
								<li v-if="isLoading" class="skeleton"></li>
								<li v-else-if="borrowerKnockouts.length === 0">N/A</li>
								<li v-for="knockout in borrowerKnockouts" :key="knockout.knockoutTypeId">
									{{ knockout.knockoutName }} |
									<span :class="knockout.decisionCssClass">{{ knockout.decision }}</span>
								</li>
							</ul>
						</div>
						<div class="list-column">
							Co-borrower
							<ul>
								<li v-if="isLoading" class="skeleton"></li>
								<li v-else-if="coBorrowerKnockouts.length === 0">N/A</li>
								<li v-for="knockout in coBorrowerKnockouts" :key="knockout.knockoutTypeId">
									{{ knockout.knockoutName }} |
									<span :class="knockout.decisionCssClass">{{ knockout.decision }}</span>
								</li>
							</ul>
						</div>
					</div>
					<div class="form-input">
						<label for="overrideScore" class="checkbox-label">
							<input
								type="checkbox"
								id="overrideScore"
								name="overrideScore"
								v-model="overrideScore"
								:disabled="isScoreCheckboxDisabled"
								:required="requiresScoreOverride" />
							<span class="custom-checkbox"></span>
							Override annual review score
						</label>
					</div>
					<!-- scores -->
					<div class="list-columns-container">
						<div class="list-column">
							Borrower
							<ul>
								<li v-if="isLoading" class="skeleton"></li>
								<li v-else-if="!borrowerScoreKnockout">N/A</li>
								<li v-else>
									{{ borrowerScoreKnockout.value }} |
									<span :class="borrowerScoreKnockout.decisionCssClass">
										{{ borrowerScoreKnockout.decision }}</span
									>
								</li>
							</ul>
						</div>
						<div class="list-column">
							Co-borrower
							<ul>
								<li v-if="isLoading" class="skeleton"></li>
								<li v-else-if="!coBorrowerScoreKnockout">N/A</li>
								<li v-else>
									{{ coBorrowerScoreKnockout.value }} |
									<span :class="coBorrowerScoreKnockout.decisionCssClass">
										{{ coBorrowerScoreKnockout.decision }}</span
									>
								</li>
							</ul>
						</div>
					</div>
				</form>
				<p>
					By clicking the <i>“Override”</i> button, I am overriding the systematic annual review decision for
					this line of credit.
				</p>
				<div v-if="errorProcessing" class="error-container">
					<ExclamationCircleIcon class="error-icon" />
					<p class="error">
						The override failed to update the LOC for Annual Review. Please close this and try again later.
					</p>
				</div>
			</div>

			<div class="modal-footer">
				<Spinner v-if="isSubmitting" />
				<button class="secondary" @click="handleClose" :disabled="isSubmitting">Cancel</button>
				<button
					@click="overrideAnnualReviewAsync"
					:disabled="!isFormValid || isSubmitting || !creditDetails || !canOverride">
					Override
				</button>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { onMounted, onUnmounted, ref, computed } from 'vue';
	import axios from 'axios';
	import functions from '@/use/functions';
	import Spinner from '@/components/shared/Spinner.vue';
	import { OverrideCreditDetails, AnnualReviewOverride } from '@/models/loans';
	import { ExclamationCircleIcon } from '@heroicons/vue/24/solid';

	const props = defineProps<{
		annualReviewId: number | undefined;
		loanId: number;
		overrides: AnnualReviewOverride[];
		creditPullTypeId: number;
		onClose: () => void;
		onOverrideSuccessAsync: () => Promise<void>;
	}>();

	const isLoading = ref<boolean>(false);
	const isSubmitting = ref<boolean>(false);
	const errorProcessing = ref<boolean>(false);

	const creditDetails = ref<OverrideCreditDetails | null>(null);

	const overrideKnockouts = ref<boolean>(false);
	const overrideScore = ref<boolean>(false);

	const canOverride = computed<boolean>(() => props.overrides.length === 0);

	const requiresKnockoutsOverride = computed(
		() => borrowerKnockouts.value.length > 0 || coBorrowerKnockouts.value.length > 0
	);

	const requiresScoreOverride = computed(
		() => borrowerScoreKnockout.value !== null || coBorrowerScoreKnockout.value !== null
	);

	const isFormValid = computed(() => {
		const hasKnockoutsOverride = requiresKnockoutsOverride.value && overrideKnockouts.value;
		const hasScoreOverride = requiresScoreOverride.value && overrideScore.value;

		return hasKnockoutsOverride || hasScoreOverride;
	});

	const isKnockoutCheckboxDisabled = computed(
		() => !requiresKnockoutsOverride.value || isLoading.value || isSubmitting.value || !canOverride.value
	);
	const isScoreCheckboxDisabled = computed(
		() => !requiresScoreOverride.value || isLoading.value || isSubmitting.value || !canOverride.value
	);

	const borrowerKnockouts = computed(
		() => creditDetails.value?.borrowerKnockouts.filter((k) => k.knockoutTypeId !== 22) || []
	);

	const coBorrowerKnockouts = computed(
		() => creditDetails.value?.coBorrowerKnockouts.filter((k) => k.knockoutTypeId !== 22) || []
	);

	const borrowerScoreKnockout = computed(
		() => creditDetails.value?.borrowerKnockouts.find((k) => k.knockoutTypeId === 22) || null
	);

	const coBorrowerScoreKnockout = computed(
		() => creditDetails.value?.coBorrowerKnockouts.find((k) => k.knockoutTypeId === 22) || null
	);

	function handleClose() {
		if (isSubmitting.value) {
			return;
		}

		props.onClose();
	}

	async function overrideAnnualReviewAsync() {
		isSubmitting.value = true;
		errorProcessing.value = false;

		try {
			const queryParams = {
				loanId: props.loanId,
				overrideKnockouts: overrideKnockouts.value,
				overrideScore: overrideScore.value
			};

			await axios.post(`/api/annualreview/${props.annualReviewId}/override`, null, { params: queryParams });

			await props.onOverrideSuccessAsync();
		} catch {
			errorProcessing.value = true;
		} finally {
			isSubmitting.value = false;
		}
	}

	async function fetchOverrideDetailsAsync() {
		isLoading.value = true;

		try {
			const queryParams = {
				creditPullTypeId: props.creditPullTypeId
			};

			const response = await axios.get<OverrideCreditDetails>(
				`/api/annualreview/${props.annualReviewId}/override`,
				{ params: queryParams }
			);

			creditDetails.value = response.data;
		} catch {
			functions.openModal({
				title: 'Oops!',
				description: `There was an issue getting the override details. Please try again later.`
			});
		} finally {
			isLoading.value = false;
		}
	}

	onMounted(async () => {
		document.body.style.overflow = 'hidden';

		// get initial overrides if they exist
		overrideKnockouts.value = props.overrides.some((o) => o.overrideTypeId === 1);
		overrideScore.value = props.overrides.some((o) => o.overrideTypeId === 2);

		await fetchOverrideDetailsAsync();
	});

	onUnmounted(() => {
		document.body.style.overflow = '';
	});
</script>

<style scoped src="@/assets/css/loans/form.css"></style>

<style scoped>
	.spinner {
		width: 35px;
		height: 35px;
	}

	.modal-overlay {
		position: fixed;
		top: 0;
		left: 0;
		width: 100vw;
		height: 100vh;
		background-color: rgba(0, 0, 0, 0.5);
		display: flex;
		justify-content: center;
		align-items: center;
		z-index: 1000000; /* stupid navbar... */
		cursor: default;
	}

	.modal-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	.modal-content {
		position: relative;
		background: white;
		padding: 20px;
		border-radius: 8px;
		min-width: 30vw;
		max-width: 40vw;
		box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
		display: flex;
		flex-direction: column;
	}

	.modal-body {
		flex-grow: 1;
		display: flex;
		flex-direction: column;
	}

	.modal-footer {
		display: flex;
		justify-content: flex-end;
		align-items: center;
		padding-top: 1rem;
		gap: 1rem;
	}

	.list-columns-container {
		display: flex;
		flex-wrap: wrap;
		padding: 0 3rem;
		width: 100%;
		columns: 2;
		gap: 1rem;
	}

	.list-column {
		flex: 1;
		min-width: fit-content;
	}

	.list-column > ul {
		margin: 0.5rem 0;
	}

	.close {
		font-size: 32px;
		cursor: pointer;
		transition: color 0.3s;
	}

	.close:hover {
		color: #888;
	}

	.close.disabled {
		pointer-events: none;
		opacity: 0.5;
		cursor: not-allowed;
	}

	button {
		padding: 10px 14px;
		background-color: black;
		color: white;
		border: none;
		border-radius: 4px;
		cursor: pointer;
		font-size: 13px;
		font-family: 'Montserrat', arial, sans-serif;
		font-weight: bold;
		transition: background-color 0.2s, color 0.2s, opacity 0.2s;
	}

	button.secondary {
		box-shadow: none;
		background-color: #ddd;
		color: black;
	}

	.form-container {
		display: flex;
		flex-direction: column;
		gap: 0;
	}

	.checkbox-label {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		cursor: pointer;
		font-weight: bold;
		font-size: 17px;
		width: fit-content;
		text-wrap: auto;
		user-select: none;
		flex-wrap: wrap;
	}

	.checkbox-label:has(input:required)::after {
		content: '*';
		color: #e31838;
		margin-left: -4px; /* half of the label gap */
	}

	.error-container {
		display: flex;
		gap: 1rem;
		align-items: center;
	}

	.error-container p {
		font-size: 16px;
		padding: 0;
		margin: 0;
		width: fit-content;
	}

	.error {
		color: #e31838 !important;
		font-style: inherit;
	}

	.error-icon {
		width: 50px;
		height: 50px;
		color: #e31838;
	}

	.info {
		color: #2e3192 !important;
	}

	.success {
		color: #48a842 !important;
	}

	.skeleton {
		background-color: #f7f7f7;
		animation: flashing 0.7s infinite alternate;
		height: 26px;
		border-radius: 4px;
	}

	@keyframes flashing {
		0% {
			background-color: #f7f7f7;
		}

		100% {
			background-color: #ccc;
		}
	}

	@media (max-width: 1024px) {
		.modal-footer {
			width: 100%;
			display: flex;
			flex-direction: column;
			justify-content: center;
		}

		.modal-content {
			width: 95vw;
			max-width: 95vw;
			height: fit-content;
		}

		.modal-body {
			flex-direction: column;
			overflow-y: auto;
		}

		button {
			min-width: 100%;
		}

		.checkbox-label {
			align-items: center;
		}

		.modal-footer {
			flex-direction: column-reverse;
		}
	}
</style>
